import React, { FC } from 'react';
import { Tooltip, IconButton } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';

interface IInfoTooltip {
  text: string;
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

/**
 * InfoTooltip component displays an info icon with a tooltip on hover.
 *
 * This component provides contextual help for form fields and other UI elements.
 * It uses Material-UI's Tooltip component with an info icon that displays
 * explanatory text when hovered or focused.
 *
 * @component
 * @param {Object} props - Component props
 * @param {string} props.text - The tooltip text to display
 * @param {'top' | 'bottom' | 'left' | 'right'} [props.placement='top'] - Tooltip placement relative to the icon
 *
 * @returns {JSX.Element} An info icon with tooltip
 *
 * @example
 * <InfoTooltip
 *   text="This field is used to target specific elements on the page"
 *   placement="right"
 * />
 */

const InfoTooltip: FC<IInfoTooltip> = ({ text, placement = 'top' }) => {
  return (
    <Tooltip
      title={text}
      placement={placement}
      arrow
      PopperProps={{
        style: { zIndex: 1600 },
      }}
    >
      <IconButton size="small" className="ml-1" aria-label={text}>
        <InfoOutlined fontSize="small" className="text-gray-500" />
      </IconButton>
    </Tooltip>
  );
};

export default InfoTooltip;
